import { Button, Typography } from '@navi/web-ui/lib/primitives';
import styles from './Agency.module.scss';
import { SearchBarInput } from '@navi/web-ui/lib/components';
import { AddIcon, CloseIcon, SearchIcon } from '@navi/web-ui/lib/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { getAgencyById, getAllAgencies } from './actions/agencyActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AGENCY_TABLE } from './actions/constants';
import { getAgencyTableColumnDefs } from './AgenciesTableData';
import AddAgencyDrawer from './components/drawer/AddAgencyDrawer';
import EditFormDrawer from './components/drawer/EditFormDrawer';
import { RowClickEvent } from '@cp/src/components/interfaces';
import { Agency, AgencyStatus } from './interfaces/types';
import Modal from '@cp/src/components/Modal/Modal';
import { setAgencyData, setIsModalOpen } from '@cp/src/reducers/AgencyDetailsSlice';
import cx from 'classnames';
import { MIN_SEARCH_VALUE_LIMIT } from './constants/constants';

const AgencyDetails = () => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showUpdateAgencyDrawer, setShowUpdateAgencyDrawer] = useState(false);
  const [showAddAgencyDrawer, setShowAddAgencyDrawer] = useState(false);
  const [agencyRefId, setAgencyRefId] = useState('');

  const toggleAddAgencyDrawer = () => {
    setShowAddAgencyDrawer(!showAddAgencyDrawer);
  };
  const handleEditAgencyClose = () => {
    setShowUpdateAgencyDrawer(false);
    setAgencyRefId('');
  };
  const currentPageData = useSelector((state: RootState) => state.agencyDashboard?.allAgencyData);
  const allAgencyPage = useSelector((state: RootState) => state.agencyDashboard?.allAgencyPage);
  const loading = useSelector((state: RootState) => state.agencyDashboard?.isLoading);
  const agencyData = useSelector((state: RootState) => state.agencyDashboard?.agencyData);
  const queryParams = readQueryParams();
  const isModalOpen = useSelector((state: RootState) => state.agencyDashboard?.isModalOpen);
  const agencyDrawerRef = useRef<React.MutableRefObject<any>>(null);

  useEffect(() => {
    const defaultParams = {
      ...queryParams[AGENCY_TABLE],
      pageSize: queryParams[AGENCY_TABLE]?.pageSize || 20,
      pageIndex: queryParams[AGENCY_TABLE]?.pageIndex || 0
    };
    dispatch(getAllAgencies(defaultParams));
  }, []);

  const updateAndNavigate = (updatedParams: Agency) => {
    const url = createQueryParams(updatedParams);
    navigate(url);
  };

  const handleSearchChange = (searchValue: string) => {
    if (!searchValue) {
      handleQueryChange('');
    }
    if (searchValue?.length >= MIN_SEARCH_VALUE_LIMIT) {
      handleQueryChange(searchValue);
    }
    return;
  };

  const handleQueryChange = (query: string) => {
    const updatedParams = { ...queryParams };
    if (!updatedParams[AGENCY_TABLE]) updatedParams[AGENCY_TABLE] = {};
    updatedParams[AGENCY_TABLE].pageIndex = '0';
    updatedParams[AGENCY_TABLE].pageSize = updatedParams?.[AGENCY_TABLE]?.pageSize || '20';
    updatedParams[AGENCY_TABLE].query = query;
    updateAndNavigate(updatedParams);
    dispatch(getAllAgencies(updatedParams?.agenciesTable));
  };

  const handlePaginationChange = (data: { pageIndex: number; pageSize: number }) => {
    const updatedParams = { ...queryParams };
    if (!updatedParams[AGENCY_TABLE]) updatedParams[AGENCY_TABLE] = {};
    updatedParams[AGENCY_TABLE].pageIndex = data.pageIndex;
    updatedParams[AGENCY_TABLE].pageSize = data.pageSize;
    dispatch(getAllAgencies(updatedParams[AGENCY_TABLE]));
  };

  const handleEditAgencyDrawerClick = (event: RowClickEvent, refId: string) => {
    event?.stopPropagation();
    setAgencyRefId(refId);
    setShowUpdateAgencyDrawer(true);
  };

  const onCasesRowClicked = (data: Agency) => {
    if (data?.status !== AgencyStatus.TERMINATED) {
      setShowUpdateAgencyDrawer(true);
      setAgencyRefId(data?.referenceId as string);
    }
  };
  const fetchAgencyData = async () => {
    dispatch(getAgencyById(agencyRefId));
  };

  useEffect(() => {
    if (!agencyRefId) {
      dispatch(setAgencyData({}));
      return;
    }
    fetchAgencyData();
  }, [agencyRefId]);

  const closeModal = () => {
    dispatch(setIsModalOpen(false));
  };
  const handleTerminateAgency = () => {
    dispatch(setIsModalOpen(false));
    if (agencyDrawerRef?.current) {
      agencyDrawerRef?.current?.handleSubmitFormClick();
    }
  };

  const columnDefs = useMemo(() => getAgencyTableColumnDefs({ handleEditAgencyDrawerClick }), []);

  const handleReset = () => {
    handleSearchChange('');
  };
  const searchValue = queryParams?.agenciesTable?.query;
  return (
    <div className={cx('flex flex-col', styles.mainContainer)}>
      <Typography variant={'h2'} className={styles.headerText}>
        Agency repository
      </Typography>
      <div className={styles.searchBarRow}>
        <SearchBarInput
          ref={searchInputRef}
          containerClassName={'m-0'}
          className={styles.searchInput}
          inputSize={'medium'}
          debounceDelay={300}
          onSearchChange={handleSearchChange}
          value={searchValue || ''}
          LeftInputAdornment={<SearchIcon style={{ cursor: 'text' }} />}
          placeholder="Search by agency name or code"
          RightInputAdornment={
            searchValue ? (
              <Button variant="text" onClick={handleReset}>
                <CloseIcon />
              </Button>
            ) : null
          }
        />
        <Button
          variant="primary"
          className={'px-[23px] py-[8px] h-9 flex items-center'}
          onClick={toggleAddAgencyDrawer}
          startAdornment={<AddIcon height={20} width={20} color="var(--tooltip-text-color)" />}
        >
          Add Agency
        </Button>
      </div>
      <div className="h-[calc(100vh-170px)]">
        <NaviTable
          tableName={AGENCY_TABLE}
          columns={columnDefs}
          data={currentPageData || []}
          totalElements={allAgencyPage?.totalElements || 0}
          loading={loading}
          onRowClick={onCasesRowClicked}
          enableRowClick={agencyData?.status === AgencyStatus.TERMINATED ? () => false : () => true}
          defaultPaginationState={{
            pageIndex: 0,
            pageSize: 20
          }}
          onPaginationChange={handlePaginationChange}
        />
      </div>
      {showAddAgencyDrawer ? (
        <AddAgencyDrawer isDrawerOpen={showAddAgencyDrawer} onClose={toggleAddAgencyDrawer} />
      ) : null}
      {showUpdateAgencyDrawer ? (
        <EditFormDrawer
          ref={agencyDrawerRef}
          isDrawerOpen={showUpdateAgencyDrawer}
          onClose={handleEditAgencyClose}
        />
      ) : null}

      {isModalOpen ? (
        <div className="w-full h-full fixed inset-0 flex justify-center items-center z-[var(--z-index-block-longhorn-warning)] p-6">
          <Modal
            visible={isModalOpen}
            onClose={closeModal}
            header={'Are you sure you want to terminate this agency?'}
            containerClasses="w-full h-full flex justify-center items-center"
            headerClassName={'text-base flex justify-between items-center !px-6 !pt-6 !pb-3'}
            showCloseIcon={false}
          >
            <div className="flex flex-col gap-10">
              <Typography variant={'p3'}>
                Status of the Agency once terminated cannot be changed
              </Typography>
              <div className="flex justify-end gap-3">
                <Button variant={'secondary'} onClick={closeModal}>
                  Cancel
                </Button>
                <Button variant={'redPrimary'} onClick={handleTerminateAgency}>
                  Yes, Terminate
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      ) : null}
    </div>
  );
};

export default AgencyDetails;
